// What counts as a SEO gap in a block, in one place: the block list, the
// block editor modal and the page list all have to agree on it, otherwise a
// block is "incomplete" in one view and finished in the next.
//
// Only two things are flagged: a picture with no alt text, and a media field
// with no file in it. Empty headings and paragraphs were flagged here at
// first, and it made the whole thing useless — on a normal page nearly every
// block leaves some text field blank on purpose, so every row went yellow.

export const MEDIA_FIELD_TYPES = ["image", "video"];

// Templates name this field differently from block to block — "Alt text",
// "Alternative text", "alt", "image_alt" — so match the schema key and the
// label alike rather than relying on one spelling.
export function isAltTextField(field) {
  return /\balt\b|alt_?text|alternative/i.test(
    `${field?.key ?? ""} ${field?.name ?? ""}`
  );
}

export function isEmptyFieldValue(value, type) {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === "string") {
    // Quill leaves an empty paragraph behind rather than an empty string.
    return value.replace(/<[^>]*>/g, "").trim().length === 0;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === "object") {
    // An image field holds either a plain URL or { src / path, name }.
    if (MEDIA_FIELD_TYPES.includes(type)) {
      return String(value.src ?? value.path ?? "").trim().length === 0;
    }
    // Any other object shape is content this check does not understand;
    // calling it empty would be a false alarm.
    return false;
  }
  return false;
}

// Translated fields are stored per language code, plain ones as they are.
export function fieldValue(settings, field, language) {
  const stored = settings?.[field?.key];
  return field?.translate ? (stored ?? {})[language] : stored;
}

// The gaps of one block, alt text first: a picture nothing describes is the
// gap search engines actually read, so it leads the list.
// Returns [{ key, name, isAlt }].
export function blockSeoGaps(fields, settings, language) {
  const usable = (fields ?? []).filter((field) => field?.key);
  // An alt text describes a picture. With no picture in the block there is
  // nothing to describe yet, so flagging it would only add noise.
  const hasMedia = usable.some(
    (field) =>
      MEDIA_FIELD_TYPES.includes(field.type) &&
      !isEmptyFieldValue(fieldValue(settings, field, language), field.type)
  );
  const gaps = [];
  for (const field of usable) {
    const isMedia = MEDIA_FIELD_TYPES.includes(field.type);
    const isAlt = isAltTextField(field);
    if (!isMedia && !isAlt) {
      continue;
    }
    if (isAlt && !hasMedia) {
      continue;
    }
    if (isEmptyFieldValue(fieldValue(settings, field, language), field.type)) {
      gaps.push({
        key: field.key,
        name: field.name ?? field.key,
        isAlt,
      });
    }
  }
  return gaps.sort((a, b) => Number(b.isAlt) - Number(a.isAlt));
}
